import { useState, useEffect } from 'react';
import styles from './astyles/TaskForm.module.css';

interface TaskFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  taskToEdit?: {
    id: number;
    title: string;
    project_id?: number; // Changed from projectId to match backend
    description: string;
    required_skills: string[];
    location: string;
    due_date?: string;
  } | null;
  projectId?: number; // New prop for project context
}

// Removed hardcoded skills - will fetch from API

interface Skill {
  id: number;
  name: string;
  description?: string;
}

interface Location {
  id: number;
  name: string;
  description?: string;
}

interface FormData {
  title: string;
  description: string;
  required_skills: string[];
  location: string;
  due_date: string;
  project_id?: number; // Added to match backend
  capacity: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

export default function TaskForm({ onSuccess, onCancel, taskToEdit, projectId }: TaskFormProps) {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    required_skills: [],
    location: '',
    due_date: '',
    project_id: projectId, // Initialize with projectId if provided
    capacity: 1
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [newSkill, setNewSkill] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addingSkill, setAddingSkill] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [tempSelectedSkills, setTempSelectedSkills] = useState<string[]>([]);

  const isEditMode = !!taskToEdit;

  // Fetch available skills and locations from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        const [skillsResponse, locationsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/skills`),
          fetch(`${API_BASE_URL}/api/locations`)
        ]);

        if (skillsResponse.ok) {
          const skills = await skillsResponse.json();
          setAvailableSkills(skills);
        }

        if (locationsResponse.ok) {
          const locations = await locationsResponse.json();
          setAvailableLocations(locations);
        }
      } catch (error) {
        console.error('Error fetching skills and locations:', error);
        setError('Failed to load skills and locations data');
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Function to add new skill
  const handleAddSkill = async () => {
    if (!newSkill.trim()) return;
    
    setAddingSkill(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/skills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1' // Admin user ID
        },
        body: JSON.stringify({ name: newSkill.trim() })
      });

      if (response.ok) {
        const newSkillData = await response.json();
        setAvailableSkills(prev => [...prev, newSkillData].sort((a, b) => a.name.localeCompare(b.name)));
        setFormData(prev => ({
          ...prev,
          required_skills: [...prev.required_skills, newSkillData.name]
        }));
        setNewSkill('');
        setShowAddSkill(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add skill');
      }
    } catch (error) {
      console.error('Error adding skill:', error);
      setError('Failed to add skill');
    } finally {
      setAddingSkill(false);
    }
  };

  // Function to add new location
  const handleAddLocation = async () => {
    if (!newLocation.trim()) return;
    
    setAddingLocation(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1' // Admin user ID
        },
        body: JSON.stringify({ name: newLocation.trim() })
      });

      if (response.ok) {
        const newLocationData = await response.json();
        setAvailableLocations(prev => [...prev, newLocationData].sort((a, b) => a.name.localeCompare(b.name)));
        setFormData(prev => ({
          ...prev,
          location: newLocationData.name
        }));
        setNewLocation('');
        setShowAddLocation(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add location');
      }
    } catch (error) {
      console.error('Error adding location:', error);
      setError('Failed to add location');
    } finally {
      setAddingLocation(false);
    }
  };

  // Function to open skills modal
  const handleOpenSkillsModal = () => {
    setTempSelectedSkills([...formData.required_skills]);
    setShowSkillsModal(true);
  };

  // Function to handle skill checkbox change
  const handleSkillCheckboxChange = (skillName: string, checked: boolean) => {
    if (checked) {
      setTempSelectedSkills(prev => [...prev, skillName]);
    } else {
      setTempSelectedSkills(prev => prev.filter(skill => skill !== skillName));
    }
  };

  // Function to confirm skills selection
  const handleConfirmSkills = () => {
    setFormData(prev => ({ ...prev, required_skills: tempSelectedSkills }));
    setShowSkillsModal(false);
  };

  // Function to cancel skills selection
  const handleCancelSkills = () => {
    setTempSelectedSkills([...formData.required_skills]);
    setShowSkillsModal(false);
  };

  // Load task data if editing
  useEffect(() => {
    if (taskToEdit) {
      setFormData({
        title: taskToEdit.title || '',
        description: taskToEdit.description || '',
        required_skills: taskToEdit.required_skills || [],
        location: taskToEdit.location || '',
        due_date: taskToEdit.due_date ? 
          new Date(taskToEdit.due_date).toISOString().split('T')[0] : '',
        project_id: taskToEdit.project_id || projectId, // Preserve existing or use context
        capacity: (taskToEdit as any).capacity || 1 // Handle capacity for existing tasks
      });
    } else if (projectId) {
      // For new tasks in project context, set project_id
      setFormData(prev => ({
        ...prev,
        project_id: projectId
      }));
    }
  }, [taskToEdit, projectId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    const endpoint = isEditMode 
      ? `${API_BASE_URL}/api/tasks/${taskToEdit?.id}`
      : projectId
        ? `${API_BASE_URL}/api/projects/${projectId}/tasks`
        : `${API_BASE_URL}/api/tasks`;
    
    const method = isEditMode ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          due_date: formData.due_date || null,
          project_id: formData.project_id || projectId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isEditMode ? 'update' : 'create'} task`);
      }

      const taskData = await response.json();
      console.log(`Task ${isEditMode ? 'updated' : 'created'}:`, taskData);
      
      // Reset form and notify parent
      if (!isEditMode) {
        setFormData({
          title: '',
          description: '',
          required_skills: [],
          location: '',
          due_date: '',
          project_id: projectId, // Maintain project context
          capacity: 1
        });
      }
      
      onSuccess?.();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} task:`, error);
      setError(error instanceof Error ? error.message : `Failed to ${isEditMode ? 'update' : 'create'} task`);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <h2>{isEditMode ? 'Edit Task' : 'Create New Task'}</h2>
      
      {error && <div className={styles.errorMessage}>{error}</div>}
      
      {/* Show project context if available */}
      {projectId && !isEditMode && (
        <div className={styles.formField}>
          <label>Project:</label>
          <input type="text" value={`Project #${projectId}`} disabled />
        </div>
      )}
      <div className={styles.formField}>
        <label htmlFor="title">Title:</label>
        <input
          id="title"
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          required
        />
      </div>
      
      <div className={styles.formField}>
        <label htmlFor="description">Description:</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          required
          rows={4}
        />
      </div>
      
      <div className={styles.formField}>
        <label htmlFor="required_skills">Required Skills:</label>
        {loadingData ? (
          <div className={styles.loadingText}>Loading skills...</div>
        ) : (
          <>
            <div className={styles.skillsSelector}>
              <div className={styles.selectedSkillsDisplay}>
                {formData.required_skills.length > 0 ? (
                  <div className={styles.selectedSkillsList}>
                    {formData.required_skills.map(skill => (
                      <span key={skill} className={styles.selectedSkillTag}>
                        {skill}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={styles.noSkillsSelected}>No skills selected</span>
                )}
              </div>
              <button
                type="button"
                onClick={handleOpenSkillsModal}
                className={styles.selectSkillsButton}
              >
                {formData.required_skills.length > 0 ? 'Change Skills' : 'Select Skills'}
              </button>
            </div>
            
            {showAddSkill && (
              <div className={styles.addNewContainer}>
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Enter new skill name"
                  className={styles.addNewInput}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                />
                <div className={styles.addNewActions}>
                  <button
                    type="button"
                    onClick={handleAddSkill}
                    disabled={addingSkill || !newSkill.trim()}
                    className={styles.addNewButton}
                  >
                    {addingSkill ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSkill(false);
                      setNewSkill('');
                    }}
                    className={styles.cancelNewButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        <small className={styles.helpText}>
          Click "Select Skills" to choose from available skills. You can also add new skills.
        </small>
      </div>
      
      <div className={styles.formField}>
        <label htmlFor="location">Location:</label>
        {loadingData ? (
          <div className={styles.loadingText}>Loading locations...</div>
        ) : (
          <>
            <select
              id="location"
              value={formData.location}
              onChange={(e) => {
                if (e.target.value === '__ADD_NEW__') {
                  setShowAddLocation(true);
                } else {
                  setFormData({...formData, location: e.target.value});
                }
              }}
              className={styles.select}
            >
              <option value="">-- Select Location --</option>
              {availableLocations.map(location => (
                <option key={location.id} value={location.name}>
                  {location.name}
                </option>
              ))}
              <option value="__ADD_NEW__" className={styles.addNewOption}>
                + Add New Location
              </option>
            </select>
            
            {showAddLocation && (
              <div className={styles.addNewContainer}>
                <input
                  type="text"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  placeholder="Enter new location name"
                  className={styles.addNewInput}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddLocation()}
                />
                <div className={styles.addNewActions}>
                  <button
                    type="button"
                    onClick={handleAddLocation}
                    disabled={addingLocation || !newLocation.trim()}
                    className={styles.addNewButton}
                  >
                    {addingLocation ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddLocation(false);
                      setNewLocation('');
                    }}
                    className={styles.cancelNewButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      <div className={styles.formField}>
        <label htmlFor="dueDate">Due Date:</label>
        <input
          id="dueDate"
          type="date"
          value={formData.due_date}
          onChange={(e) => setFormData({...formData, due_date: e.target.value})}
        />
      </div>
      
      <div className={styles.formField}>
        <label htmlFor="capacity">Capacity:</label>
        <input
          id="capacity"
          type="number"
          min="1"
          value={formData.capacity}
          onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value) || 1})}
          placeholder="Number of participants you require for the task"
          required
        />
        <small className={styles.helpText}>
          Number of participants you require for the task
        </small>
      </div>
      <div className={styles.formActions}>
        <button 
          type="button" 
          className={styles.cancelButton}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className={styles.submitButton}
          disabled={isSubmitting || !formData.title || !formData.description}
        >
          {isSubmitting 
            ? (isEditMode ? 'Updating...' : 'Creating...') 
            : (isEditMode ? 'Update Task' : 'Create Task')}
        </button>
      </div>

      {/* Skills Selection Modal */}
      {showSkillsModal && (
        <div className={styles.skillsModal}>
          <div className={styles.skillsModalContent}>
            <div className={styles.skillsModalHeader}>
              <h3>Select Required Skills</h3>
              <button
                type="button"
                onClick={handleCancelSkills}
                className={styles.closeModalButton}
              >
                ×
              </button>
            </div>
            
            <div className={styles.skillsModalBody}>
              <div className={styles.skillsCheckboxList}>
                {availableSkills.map(skill => (
                  <label key={skill.id} className={styles.skillCheckboxItem}>
                    <input
                      type="checkbox"
                      checked={tempSelectedSkills.includes(skill.name)}
                      onChange={(e) => handleSkillCheckboxChange(skill.name, e.target.checked)}
                    />
                    <span className={styles.skillCheckboxLabel}>{skill.name}</span>
                  </label>
                ))}
              </div>
              
              <div className={styles.addNewSkillInModal}>
                <button
                  type="button"
                  onClick={() => setShowAddSkill(!showAddSkill)}
                  className={styles.addNewSkillButton}
                >
                  {showAddSkill ? 'Cancel Add New' : '+ Add New Skill'}
                </button>
                
                {showAddSkill && (
                  <div className={styles.addNewSkillForm}>
                    <input
                      type="text"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="Enter new skill name"
                      className={styles.addNewSkillInput}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                    />
                    <button
                      type="button"
                      onClick={handleAddSkill}
                      disabled={addingSkill || !newSkill.trim()}
                      className={styles.addNewSkillSubmit}
                    >
                      {addingSkill ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.skillsModalFooter}>
              <button
                type="button"
                onClick={handleCancelSkills}
                className={styles.cancelSkillsButton}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSkills}
                className={styles.confirmSkillsButton}
              >
                OK ({tempSelectedSkills.length} selected)
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
     